import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiResponse } from '@types/index';
import { authService } from './authService';

/**
 * Base API client configuration
 * Handles authentication, error handling, and request/response interceptors
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - redirect to login
          this.handleUnauthorized();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get authentication token from Keycloak
   */
  private getAuthToken(): string | null {
    return authService.getToken() || null;
  }

  /**
   * Handle unauthorized access
   */
  private handleUnauthorized(): void {
    // Logout through Keycloak and redirect to login
    authService.logout().catch(() => {
      // Fallback if logout fails
      window.location.href = '/login';
    });
  }

  /**
   * Generic GET request
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const response = await this.client.get<T>(url, config);
    return response;
  }

  /**
   * Generic POST request
   */
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const response = await this.client.post<T>(url, data, config);
    return response;
  }

  /**
   * Generic PUT request
   */
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const response = await this.client.put<T>(url, data, config);
    return response;
  }

  /**
   * Generic PATCH request
   */
  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const response = await this.client.patch<T>(url, data, config);
    return response;
  }

  /**
   * Generic DELETE request
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const response = await this.client.delete<T>(url, config);
    return response;
  }

  /**
   * Handle API errors
   */
  private handleError<T>(error: any): ApiResponse<T> {
    if (axios.isAxiosError(error) && error.response) {
      return {
        success: false,
        error: {
          code: error.response.data?.error?.code || 'API_ERROR',
          message: error.response.data?.error?.message || error.message,
          details: error.response.data?.error?.details,
        },
      };
    }

    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'An unexpected error occurred',
      },
    };
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
